"use client"

import { useSound } from "@/hooks/use-sound"
import { cn } from "@/lib/util/cn"

interface SoundToggleProps {
  className?: string
  showVolumeSlider?: boolean
}

export function SoundToggle({
  className,
  showVolumeSlider = false,
}: SoundToggleProps) {
  const { soundEnabled, volume, toggleSound, setVolume, hasUserInteracted } =
    useSound()

  if (!hasUserInteracted) {
    return null
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <button
        onClick={toggleSound}
        className={cn(
          "flex items-center justify-center rounded-full p-2 transition-colors",
          "hover:bg-black/10 dark:hover:bg-white/10",
          "focus:ring-2 focus:ring-black/20 focus:outline-none dark:focus:ring-white/20",
          soundEnabled
            ? "text-black dark:text-white/80"
            : "text-black/50 dark:text-white/50",
        )}
        aria-label={soundEnabled ? "Mute audio" : "Unmute audio"}
      >
        {soundEnabled ? (
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5 15H3a1 1 0 01-1-1v-4a1 1 0 011-1h2l4-4v14l-4-4z"
            />
          </svg>
        ) : (
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
            />
          </svg>
        )}
      </button>

      {showVolumeSlider && soundEnabled && (
        <div className="flex items-center gap-2">
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className={cn(
              "h-1 w-20 cursor-pointer appearance-none rounded-full bg-black/20 dark:bg-white/20",
              "slider:h-3 slider:w-3 slider:appearance-none slider:rounded-full slider:bg-black dark:slider:bg-white",
            )}
            aria-label="Volume"
          />
          <span className="text-xs text-black/70 tabular-nums dark:text-white/70">
            {Math.round(volume * 100)}%
          </span>
        </div>
      )}
    </div>
  )
}
